import { type JSX, useRef, useState } from 'react';
import { sleep } from '@chainflip/utils/async';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { Link } from '@/shared/components/atoms/Link';
import { DashboardModalWithCloseButton } from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import ArrowIcon from '@/shared/icons/flip-ui-kit/large/Arrow';
import ArrowIconSmall from '@/shared/icons/flip-ui-kit/small/Arrow';
import InformationIcon from '@/shared/icons/flip-ui-kit/small/Information';
import { defaultAnimationProps } from '@/shared/utils/consts';
import { LIQUIDATIONS_QUERY } from '@/shared/utils/lending';
import { formatUsdValue } from '@/shared/utils/numbers';
import { formatBps } from '@/shared/utils/strings';

import useLoanAccount from 'packages/liquidity-provision/hooks/useLoanAccount';
import {
  useExtrinsicSDK,
  useStateChainAccount,
} from 'packages/liquidity-provision/hooks/useStateChainAccount';
import SuccessIllustration from '../../../public/success-illustration.svg';

type PreviousState = {
  oldTotalBorrowAmountUsd: number;
  oldLoanToValueBps: number;
};

const Success = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col gap-6 rounded-md">
    <div className="my-auto flex w-full flex-col items-center justify-center">
      <div className="mb-[-74px]">
        <SuccessIllustration />
      </div>
      <div className="flex flex-col items-center gap-y-2 font-aeonikMedium">
        <span className="text-24 text-cf-white">Liquidation started</span>
        <span className="max-w-[266px] text-center text-16 text-cf-light-3">
          Your collateral will be sold gradually to reduce your outstanding debt
        </span>
      </div>
    </div>
    {children}
  </div>
);

interface ManualLiquidationModalProps {
  open: boolean;
  onClose: () => void;
}

const InfoSuccessRow = ({
  label,
  firstValue,
  secondValue,
  tooltip,
}: {
  label: string;
  firstValue: string;
  secondValue: string;
  tooltip: string;
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-x-1 text-14">
      {label} <QuestionMarkTooltip content={tooltip} />
    </div>
    <div className="flex items-center gap-x-1">
      <span>{firstValue}</span>
      <ArrowIconSmall />
      <span className="text-cf-white">{secondValue}</span>
    </div>
  </div>
);

const InfoRow = ({
  label,
  firstValue,
  secondValue,
  tooltip,
}: {
  label: string;
  firstValue: string;
  secondValue: string;
  tooltip: string;
}) => (
  <div className="flex flex-col gap-y-2">
    <div className="flex items-center gap-x-1 text-14 text-cf-light-2">
      <span>{label}</span>
      <QuestionMarkTooltip content={tooltip} />
    </div>
    <div className="flex items-center gap-x-1 text-20 text-cf-light-2">
      <span>{firstValue}</span>
      <ArrowIcon />
      <span className="text-cf-white">{secondValue}</span>
    </div>
  </div>
);

export const ManualLiquidationModal = ({
  open,
  onClose,
}: ManualLiquidationModalProps): JSX.Element => {
  const { refreshAccountInfo } = useStateChainAccount();
  const [prevValues, setPrevValues] = useState<PreviousState>();
  const focusRef = useRef<HTMLButtonElement>(null);
  const { toast, sendExtrinsic } = useExtrinsicSDK('initiateVoluntaryLiquidation');
  const queryClient = useQueryClient();

  const {
    loanAccount: { isLiquidating, totalBorrowAmountUsd, loanToValueBps },
  } = useLoanAccount();

  const {
    mutate,
    isPending,
    isSuccess,
    reset: resetMutation,
  } = useMutation({
    mutationFn: async () => {
      setPrevValues({
        oldTotalBorrowAmountUsd: totalBorrowAmountUsd,
        oldLoanToValueBps: loanToValueBps,
      });
      return sendExtrinsic();
    },
    async onSuccess() {
      await sleep(500);
      await Promise.all(
        [LIQUIDATIONS_QUERY, 'cf_loan_accounts'].map((query) =>
          queryClient.invalidateQueries({ queryKey: [query], exact: false }),
        ),
      );
      await refreshAccountInfo();
      toast.success(`Liquidation process started`);
    },
    onError(err) {
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });

  return (
    <DashboardModalWithCloseButton
      initialFocus={focusRef}
      open={open}
      onClose={onClose}
      afterLeave={() => {
        resetMutation();
        setPrevValues(undefined);
      }}
    >
      <AnimatePresence mode="wait">
        {isSuccess ? (
          <motion.div key="supply-success" {...defaultAnimationProps}>
            <Success>
              <div className="flex flex-col gap-y-3 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-14 text-cf-light-2 shadow-grayPop3">
                <InfoSuccessRow
                  label="Outstanding Debt"
                  firstValue={formatUsdValue(prevValues?.oldTotalBorrowAmountUsd ?? 0)}
                  secondValue={formatUsdValue(0)}
                  tooltip="The total amount you've borrowed, including fees accrued."
                />
                <InfoSuccessRow
                  label="Loan-to-Value (LTV)"
                  firstValue={formatBps(prevValues?.oldLoanToValueBps ?? 0)}
                  secondValue={formatBps(0)}
                  tooltip="The ratio between the value of your loan and the value of your collateral. A higher LTV means less borrowing power and higher liquidation risk"
                />
              </div>
              <Button
                className="w-full"
                type="primary-standard"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
              >
                View my Position
              </Button>
            </Success>
          </motion.div>
        ) : (
          <motion.div key="supply-form" {...defaultAnimationProps}>
            <div className="flex max-w-[437px] flex-col gap-y-6">
              <div className="flex flex-col gap-y-3">
                <div className="font-aeonikMedium text-16 text-cf-white">
                  Liquidate Collateral Balance
                </div>
                <div className="text-14 text-cf-light-2">
                  This process sells your collateral to repay your entire outstanding debt.{' '}
                  <Link
                    href="https://docs.chainflip.io/lending/understanding_liquidations"
                    target="_blank"
                    color="green"
                  >
                    Learn more.
                  </Link>
                </div>
              </div>
              <div className="flex flex-col gap-y-3">
                <div className="flex items-center gap-x-1 font-aeonikMedium text-white">
                  <InformationIcon />
                  <span className="text-14 text-cf-light-2">
                    You can stop this process any time
                  </span>
                </div>

                <div className="flex items-center gap-x-1 font-aeonikMedium text-white">
                  <InformationIcon />
                  <span className="text-14 text-cf-light-2">
                    Any remaining value stays in your collateral balance
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-y-4 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium transition duration-100 ease-in">
                <InfoRow
                  label="Outstanding Debt"
                  firstValue={formatUsdValue(totalBorrowAmountUsd)}
                  secondValue={formatUsdValue(0)}
                  tooltip="The total amount you've borrowed, including fees accrued."
                />
                <InfoRow
                  label="Loan-to-Value (LTV)"
                  firstValue={formatBps(loanToValueBps)}
                  secondValue={formatBps(0)}
                  tooltip="The ratio between the value of your loan and the value of your collateral. A higher LTV means less borrowing power and higher liquidation risk"
                />
              </div>
              <Button
                fullWidth
                onClick={() => mutate()}
                loading={isPending && 'iconOnly'}
                disabled={isPending || isLiquidating}
              >
                Start Liquidation
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardModalWithCloseButton>
  );
};
